import type {
  CanalComunicacaoCertificado,
  PartePainelComprovacao,
  ResumoComprovacao,
  VarianteStatusCertificado,
} from '../../../types/certificadoAcessosTypes';
import { CanalIcone } from '../documento/CanalIcone';

const PONTO: Record<VarianteStatusCertificado, string> = {
  success: 'bg-success-600',
  warning: 'bg-warning-500',
  error: 'bg-error-500',
  neutral: 'bg-neutral-400',
};

const TEXTO: Record<VarianteStatusCertificado, string> = {
  success: 'text-success-700',
  warning: 'text-warning-700',
  error: 'text-error-600',
  neutral: 'text-neutral-500',
};

const SITUACAO_FUNDO: Record<VarianteStatusCertificado, string> = {
  success: 'bg-success-50 text-success-700 border-success-200',
  warning: 'bg-warning-50 text-warning-800 border-warning-200',
  error: 'bg-error-50 text-error-600 border-error-200',
  neutral: 'bg-neutral-100 text-neutral-600 border-neutral-200',
};

// Rótulos curtos para caber no cabeçalho da tabela.
const CANAL_CURTO: Record<CanalComunicacaoCertificado, string> = {
  WHATSAPP: 'WhatsApp',
  EMAIL: 'E-mail',
  RCS: 'RCS',
  CORREIOS: 'Correios',
  PLATAFORMA: 'Plataforma',
};

const CANAIS_ORDEM: CanalComunicacaoCertificado[] = [
  'WHATSAPP',
  'EMAIL',
  'RCS',
  'CORREIOS',
  'PLATAFORMA',
];

const parteTemRcs = (parte: PartePainelComprovacao): boolean =>
  parte.canais.some((canal) => canal.canal === 'RCS');

/**
 * A coluna do RCS só aparece quando alguma parte recebeu o cartão: o canal nasceu com a régua da
 * citação, e os certificados de antes dela continuam com as quatro colunas de sempre.
 */
function canaisComRegistro(resumo: ResumoComprovacao): CanalComunicacaoCertificado[] {
  if (resumo.partes.some(parteTemRcs)) return CANAIS_ORDEM;
  return CANAIS_ORDEM.filter((canal) => canal !== 'RCS');
}

// Classes estáticas por quantidade de canais — o JIT do Tailwind não resolve grid-cols montado
// dinamicamente em runtime.
const COLS_POR_QUANTIDADE_DE_CANAIS: Record<number, string> = {
  1: 'grid-cols-[2fr_1fr] gap-x-3',
  2: 'grid-cols-[2fr_1fr_1fr] gap-x-3',
  3: 'grid-cols-[2fr_1fr_1fr_1fr] gap-x-3',
  4: 'grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-x-3',
  5: 'grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] gap-x-3',
};

const TEXTO_INTRO_PADRAO =
  'Situação de cada parte requerida por canal, com destaque para a confirmação de leitura. O registro cronológico completo, com metadados, está na seção de Detalhes de Auditoria.';

interface SecaoEventosIntroProps {
  texto?: string;
}

export function SecaoEventosIntro({ texto = TEXTO_INTRO_PADRAO }: SecaoEventosIntroProps) {
  return (
    <div className="px-[37px]">
      <p className="mb-[18px] text-[14px] leading-[1.6] text-neutral-700">{texto}</p>
    </div>
  );
}

interface PainelComprovacaoProps {
  resumo: ResumoComprovacao;
  canais?: CanalComunicacaoCertificado[];
  tituloColunaParte?: string;
}

export function PainelComprovacao({
  resumo,
  canais,
  tituloColunaParte = 'Parte requerida',
}: PainelComprovacaoProps) {
  const canaisExibidos = canais ?? canaisComRegistro(resumo);
  const cols =
    COLS_POR_QUANTIDADE_DE_CANAIS[canaisExibidos.length] ?? COLS_POR_QUANTIDADE_DE_CANAIS[4];

  return (
    <div className="px-[37px] pb-[26px]">
      <div className="overflow-hidden rounded-[8px] border border-solid border-neutral-200">
        {/* Cabeçalho */}
        <div
          className={`grid ${cols} border-b border-solid border-neutral-200 bg-neutral-100 px-[14px] py-[8px] text-[10px] font-semibold tracking-[0.4px] text-neutral-500 uppercase`}
        >
          <div>{tituloColunaParte}</div>
          {canaisExibidos.map((canal) => (
            <div key={canal} className="flex items-center gap-[5px]">
              <CanalIcone canal={canal} className="h-[12px] w-[12px]" />
              {CANAL_CURTO[canal]}
            </div>
          ))}
        </div>

        {/* Linhas por parte */}
        {resumo.partes.map((parte, index) => {
          const canalPorTipo = new Map(parte.canais.map((canal) => [canal.canal, canal]));
          return (
            <div
              key={parte.nome}
              className={`grid ${cols} items-center px-[14px] py-[11px] ${
                index < resumo.partes.length - 1 ? 'border-b border-solid border-neutral-200' : ''
              }`}
            >
              <div className="pr-2">
                <div className="text-[12px] font-bold text-neutral-800">{parte.nome}</div>
                <div className="mb-[5px] text-[10px] text-neutral-500">{parte.documento}</div>
                <span
                  className={`inline-flex items-center gap-[5px] rounded-full border border-solid px-[8px] py-[2px] text-[10.5px] font-semibold ${SITUACAO_FUNDO[parte.situacaoVariante]}`}
                >
                  <span
                    className={`h-[6px] w-[6px] rounded-full ${PONTO[parte.situacaoVariante]}`}
                  />
                  {parte.situacaoLabel}
                </span>
                {parte.linhasDaCitacao.length > 0 && (
                  <div className="mt-[3px] flex flex-col gap-[2px] text-[10px] leading-[1.4] text-neutral-500">
                    {parte.linhasDaCitacao.map((linha, indice) => (
                      <span key={`${indice}-${linha}`}>{linha}</span>
                    ))}
                  </div>
                )}
              </div>
              {canaisExibidos.map((tipo) => {
                const canal = canalPorTipo.get(tipo);
                if (!canal) {
                  return (
                    <div key={tipo} className="text-[11px] text-neutral-400">
                      –
                    </div>
                  );
                }
                return (
                  <div
                    key={tipo}
                    className={`flex items-center gap-[5px] text-[11px] font-medium ${TEXTO[canal.variante]}`}
                  >
                    <span
                      className={`h-[6px] w-[6px] flex-none rounded-full ${PONTO[canal.variante]}`}
                    />
                    {canal.statusLabel}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
